import { useEffect, useState } from "react";
import { Button, DirectoryInput, DropdownMenu, SwitchSelect } from "@repo/ui";
import { cn } from "@repo/utils";
import { useUpdateBrandPaths } from "./use-update-brand-path";

export type InputMode = "create" | "edit";

type Props = {
  path: string;
  index: number;
  onChangePath: (value: string) => void;
  includeSubFolders: boolean;
  onChangeIncludeSubFolders: (value: boolean) => void;
  onClickCancel: () => void;
  mode?: InputMode;
  brandId?: number;
  isNew?: boolean;
};

export const BrandPathInput = ({
  path,
  index,
  onChangePath,
  includeSubFolders,
  onChangeIncludeSubFolders,
  onClickCancel,
  mode = "create",
  brandId = undefined,
  isNew = false,
}: Props) => {
  const [isEditing, setIsEditing] = useState(isNew);
  const showActions = mode === "create" || (mode === "edit" && isEditing);
  const { updateBrandPath, isError, isPending, isSuccess } = useUpdateBrandPaths();

  useEffect(() => {
    if (isSuccess || isError) {
      setIsEditing(false);
    }
  }, [isError, isSuccess]);

  const cancelActionMap: Record<InputMode, () => void> = {
    create: onClickCancel,
    edit: () => (isNew ? onClickCancel() : setIsEditing(false)),
  };

  return (
    <div className="card-border flex items-center gap-10 p-4">
      <DirectoryInput
        apiBaseUrl={import.meta.env.VITE_FS_API_URL}
        className={cn({
          "cursor-default! border-none bg-white! opacity-100!": mode === "edit" && !isEditing,
        })}
        disabled={mode === "edit" && !isEditing}
        value={path}
        onChange={(newPath) => onChangePath(newPath ?? "")}
        placeholder={isNew || mode === "create" ? "Select a folder..." : ""}
        showClearIcon
      />
      <div className="flex items-center gap-4">
        <SwitchSelect
          disabled={mode === "edit" && !isEditing}
          checked={includeSubFolders}
          onCheckedChange={onChangeIncludeSubFolders}
          labelClassName="mb-0 text-nowrap"
          containerClassName="flex items-center gap-2"
          label="Include subfolders?"
        />
        <div className="flex items-center gap-2">
          <span className="h-[40px] w-[1px] border border-gray-300" />
          {showActions ? (
            <>
              {mode === "edit" && (
                <Button
                  disabled={isPending}
                  className="text-blue-700"
                  onClick={() =>
                    updateBrandPath(
                      isNew ? "create" : mode,
                      index,
                      brandId,
                      path,
                      includeSubFolders
                    )
                  }
                  variant="ghost"
                >
                  Save
                </Button>
              )}
              <Button disabled={isPending} onClick={cancelActionMap[mode]} variant="ghost">
                Cancel
              </Button>
            </>
          ) : (
            <DropdownMenu
              items={[
                { id: "edit", label: "Edit", onClick: () => setIsEditing(true) },
                {
                  id: "delete",
                  label: "Delete",
                  onClick: () => updateBrandPath("delete", index, brandId, path, includeSubFolders),
                },
              ]}
            />
          )}
        </div>
      </div>
    </div>
  );
};
